import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import { API, AuthContext } from '../App';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  ArrowRightLeft,
  Search,
  Trash2,
  Send,
  RefreshCw,
  CheckCircle2,
  Package,
  Building2,
  Inbox,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Camera,
  X,
  Flashlight,
  ZoomIn,
} from 'lucide-react';

// ==========================================
// FULLSCREEN BARCODE SCANNER COMPONENT
// ==========================================
const FullscreenScanner = ({ onScan, onClose }) => {
  const html5Ref = useRef(null);
  const [torchOn, setTorchOn] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);
  const containerId = 'transfer-barcode-scanner';

  useEffect(() => {
    let mounted = true;

    const startScanner = async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: 'environment' }
            });
            stream.getTracks().forEach(t => t.stop());
          } catch (err) {
            console.warn('Camera permission check failed:', err);
          }
        }

        const scanner = new Html5Qrcode(containerId, {
          verbose: false,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true }
        });

        html5Ref.current = scanner;
        
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 15,
            qrbox: { width: 280, height: 160 },
            aspectRatio: 1.777,
            disableFlip: false,
          },
          (decodedText) => {
            if (mounted) {
              onScan(decodedText);
            }
          },
          () => {}
        );
      } catch (err) {
        console.error('Scanner start error:', err);
        if (mounted) {
          setError('Não foi possível aceder à câmara. Verifique as permissões.');
        }
      }
    };

    startScanner();

    return () => {
      mounted = false;
      if (html5Ref.current) {
        html5Ref.current.stop().catch(() => {});
      }
    };
  }, [onScan]);

  const toggleTorch = async () => {
    if (!html5Ref.current) return;
    try {
      const videoElement = document.querySelector(`#${containerId} video`);
      if (videoElement && videoElement.srcObject) {
        const track = videoElement.srcObject.getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        if (capabilities.torch) {
          await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
          setTorchOn(!torchOn);
        }
      }
    } catch (err) {
      console.warn('Torch toggle failed:', err);
    }
  };

  const handleZoom = async () => {
    if (!html5Ref.current) return;
    try {
      const videoElement = document.querySelector(`#${containerId} video`);
      if (videoElement && videoElement.srcObject) {
        const track = videoElement.srcObject.getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        if (capabilities.zoom) {
          const newZoom = zoom >= capabilities.zoom.max ? capabilities.zoom.min : zoom + 1;
          await track.applyConstraints({ advanced: [{ zoom: newZoom }] });
          setZoom(newZoom);
        }
      }
    } catch (err) {
      console.warn('Zoom failed:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex justify-between items-center p-4 bg-black/50">
        <h2 className="text-white font-medium">Ler Código de Barras</h2>
        <button
          onClick={onClose}
          className="text-white p-2 hover:bg-white/10 rounded-full"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
      
      <div className="flex-1 relative">
        {error ? (
          <div className="flex items-center justify-center h-full text-white p-4 text-center">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div id={containerId} className="w-full h-full" />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-72 h-40 border-2 border-white/50 rounded-lg relative">
                <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-lg" />
              </div>
            </div>
          </>
        )}
      </div>
      
      <div className="p-4 bg-black/50 flex justify-center gap-6">
        <button
          onClick={toggleTorch}
          className={`p-3 rounded-full ${torchOn ? 'bg-yellow-500' : 'bg-white/20'}`}
        >
          <Flashlight className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={handleZoom}
          className="p-3 rounded-full bg-white/20"
        >
          <ZoomIn className="w-6 h-6 text-white" />
        </button>
      </div>
    </div>
  );
};

// Store-initiated TAR: transfer stock from the user's own warehouse to ANY other.
// Users with `tar.create_any` (admin) can pick ANY source warehouse; everyone
// else is restricted to their allowed_warehouses (also enforced server-side).
export default function StoreTransfer() {
  const { user, hasPermission } = useContext(AuthContext);
  const canChooseAnySource = hasPermission('tar.create_any');
  
  // Get user's reference warehouse (canonical) with fallback to legacy
  const userReferenceWarehouse = user?.reference_warehouse || user?.default_warehouse || '';
  
  // Get user's allowed warehouses for source selection
  const userAllowedWarehouses = useMemo(() => {
    const allowed = new Set();
    const refWh = user?.reference_warehouse || user?.default_warehouse;
    if (refWh) allowed.add(refWh);
    if (Array.isArray(user?.allowed_warehouses)) {
      user.allowed_warehouses.forEach(wh => allowed.add(wh));
    }
    return Array.from(allowed);
  }, [user]);
  
  const [sourceCode, setSourceCode] = useState(userReferenceWarehouse);

  const [allWarehouses, setAllWarehouses] = useState([]);
  const [targetCode, setTargetCode] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [cart, setCart] = useState([]);

  // Product search
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Barcode scanner
  const [showScanner, setShowScanner] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);

  // History (outbox)
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Inbox (pending receipts at MY warehouse)
  const [inbox, setInbox] = useState([]);
  const [inboxPendingCount, setInboxPendingCount] = useState(0);
  const [includeReceived, setIncludeReceived] = useState(false);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [receiptTransfer, setReceiptTransfer] = useState(null);
  const [receiptItems, setReceiptItems] = useState([]);  // [{sku, expected, received}]
  const [receiptNotes, setReceiptNotes] = useState('');
  const [confirmingReceipt, setConfirmingReceipt] = useState(false);

  // Expanded transfer ID for viewing items
  const [expandedTransferId, setExpandedTransferId] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  const fetchWarehouses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/warehouses`);
      const all = (res.data?.warehouses || []);
      all.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
      setAllWarehouses(all);
    } catch {
      toast.error('Erro ao carregar armazéns');
    }
  }, []);

  const warehouses = useMemo(
    () => allWarehouses.filter((w) => w.code !== sourceCode),
    [allWarehouses, sourceCode],
  );

  const changeSource = (code) => {
    if (code === sourceCode) return;
    setSourceCode(code);
    setCart([]);
    setSearch('');
    setSearchResults([]);
    if (targetCode === code) setTargetCode('');
  };

  const fetchHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const res = await axios.get(`${API}/store/transfers?limit=20`);
      setHistory(res.data?.transfers || []);
    } catch {
      // silent — empty list will show
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const fetchInbox = useCallback(async (withReceived = includeReceived) => {
    try {
      setLoadingInbox(true);
      const res = await axios.get(`${API}/store/transfers/inbox`, {
        params: { include_received: withReceived },
      });
      setInbox(res.data?.transfers || []);
      setInboxPendingCount(res.data?.pending_count || 0);
    } catch {
      // silent
    } finally {
      setLoadingInbox(false);
    }
  }, [includeReceived]);

  useEffect(() => { fetchWarehouses(); fetchHistory(); fetchInbox(false); }, [fetchWarehouses, fetchHistory, fetchInbox]);

  // Debounced product search — restricted to items with stock in our source warehouse
  // Increased page_size to show more results for partial matches
  useEffect(() => {
    const q = search.trim();
    if (!q || !sourceCode) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await axios.get(`${API}/products`, {
          params: {
            page: 1,
            page_size: 100,  // Increased to show all relevant matches for prefix searches
            search: q,
            warehouse: sourceCode,
            sort_by: 'sku',
            sort_order: 'asc',
          },
        });
        // Sort results: prioritize SKUs that START with the search term
        const items = res.data?.items || [];
        const qLower = q.toLowerCase();
        items.sort((a, b) => {
          const aStarts = (a.sku || '').toLowerCase().startsWith(qLower);
          const bStarts = (b.sku || '').toLowerCase().startsWith(qLower);
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return (a.sku || '').localeCompare(b.sku || '');
        });
        setSearchResults(items); // Show all results after sorting
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [search, sourceCode]);
  
  // Handle barcode scan
  const handleBarcodeScan = async (code) => {
    setShowScanner(false);
    if (!sourceCode) {
      toast.error('Selecione primeiro o armazém de origem');
      return;
    }
    
    setScannerLoading(true);
    try {
      // First try exact barcode lookup
      const barcodeRes = await axios.get(`${API}/products/by-barcode/${encodeURIComponent(code)}`);
      if (barcodeRes.data) {
        const product = barcodeRes.data;
        const sourceStock = Number((product.stock_by_warehouse || {})[sourceCode] || 0);
        if (sourceStock > 0) {
          addToCart(product);
          toast.success(`Artigo adicionado: ${product.sku}`);
        } else {
          toast.error(`${product.sku} sem stock em ${sourceCode}`);
        }
        return;
      }
    } catch (e) {
      // Barcode not found, try SKU search
    }
    
    // Fallback: search by SKU
    try {
      const res = await axios.get(`${API}/products`, {
        params: {
          search: code,
          warehouse: sourceCode,
          page_size: 1,
        },
      });
      const items = res.data?.items || [];
      if (items.length > 0) {
        const product = items[0];
        // Check if it's an exact SKU match
        if (product.sku.toLowerCase() === code.toLowerCase()) {
          const sourceStock = Number((product.stock_by_warehouse || {})[sourceCode] || 0);
          if (sourceStock > 0) {
            addToCart(product);
            toast.success(`Artigo adicionado: ${product.sku}`);
          } else {
            toast.error(`${product.sku} sem stock em ${sourceCode}`);
          }
        } else {
          toast.warning(`Código não encontrado: ${code}`);
        }
      } else {
        toast.error(`Artigo não encontrado: ${code}`);
      }
    } catch {
      toast.error('Erro ao procurar artigo');
    } finally {
      setScannerLoading(false);
    }
  };

  const sourceStockFor = (product) =>
    Number((product.stock_by_warehouse || {})[sourceCode] || 0);

  const addToCart = (product) => {
    const sourceStock = sourceStockFor(product);
    if (sourceStock <= 0) {
      toast.error(`Sem stock de ${product.sku} no seu armazém`);
      return;
    }
    setCart((prev) => {
      const existing = prev.find((c) => c.sku === product.sku);
      if (existing) {
        if (existing.quantity >= sourceStock) {
          toast.warning(`Já adicionaste o stock todo de ${product.sku} (${sourceStock})`);
          return prev;
        }
        return prev.map((c) => c.sku === product.sku ? { ...c, quantity: c.quantity + 1 } : c);
      }
      return [...prev, {
        sku: product.sku,
        name: product.name,
        quantity: 1,
        sourceStock,
      }];
    });
    setSearch('');
    setSearchResults([]);
  };

  const updateQty = (sku, delta) => {
    setCart((prev) => prev.flatMap((c) => {
      if (c.sku !== sku) return [c];
      const next = c.quantity + delta;
      if (next <= 0) return [];
      if (next > c.sourceStock) {
        toast.warning(`Stock máximo de ${c.sku} é ${c.sourceStock}`);
        return [c];
      }
      return [{ ...c, quantity: next }];
    }));
  };

  const removeFromCart = (sku) => {
    setCart((prev) => prev.filter((c) => c.sku !== sku));
  };

  const totalItems = useMemo(() => cart.reduce((sum, c) => sum + c.quantity, 0), [cart]);

  const submitTransfer = async () => {
    if (!sourceCode) { toast.error('Escolhe o armazém de origem'); return; }
    if (!targetCode) { toast.error('Escolhe o armazém de destino'); return; }
    if (!cart.length) { toast.error('Adiciona pelo menos um artigo'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/store/transfer-tar`, {
        target_warehouse: targetCode,
        source_warehouse: sourceCode,
        items: cart.map((c) => ({ sku: c.sku, quantity: c.quantity })),
        observacoes: observacoes.trim() || undefined,
      });
      const data = res.data || {};
      toast.success(`✅ TAR criada no SAGE: ${data.tar_reference}`);
      // Reset and refresh
      setCart([]);
      setTargetCode('');
      setObservacoes('');
      fetchHistory();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro a criar TAR';
      toast.error(`Falha SAGE: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Receipt confirmation (inbox) ──────────────────────────────────
  const openReceiptDialog = (transfer) => {
    setReceiptTransfer(transfer);
    setReceiptItems((transfer.items || []).map((it) => ({
      sku: it.sku,
      name: it.name,
      expected: it.quantity,
      received: it.quantity,
    })));
    setReceiptNotes('');
  };

  const updateReceiptQty = (sku, value) => {
    const v = Math.max(0, parseInt(value, 10) || 0);
    setReceiptItems((prev) => prev.map((i) => i.sku === sku ? { ...i, received: v } : i));
  };

  const submitReceipt = async () => {
    if (!receiptTransfer) return;
    setConfirmingReceipt(true);
    try {
      const payload = {
        items: receiptItems.map((i) => ({ sku: i.sku, received_quantity: i.received })),
        notes: receiptNotes.trim() || undefined,
      };
      const res = await axios.post(
        `${API}/store/transfers/${receiptTransfer.id}/confirm-receipt`,
        payload,
      );
      const status = res.data?.status;
      if (status === 'with_discrepancy') {
        toast.warning('Receção confirmada com discrepâncias');
      } else {
        toast.success('✅ Receção confirmada');
      }
      setReceiptTransfer(null);
      fetchInbox();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Erro a confirmar receção';
      toast.error(msg);
    } finally {
      setConfirmingReceipt(false);
    }
  };

  const hasDiscrepancy = useMemo(() =>
    receiptItems.some((i) => i.received !== i.expected),
    [receiptItems],
  );

  if (!sourceCode && !canChooseAnySource && userAllowedWarehouses.length === 0) {
    return (
      <div className="p-6 text-center text-slate-500" data-testid="store-transfer-no-warehouse">
        <p className="text-sm">Sem armazém atribuído ao utilizador. Contacta o administrador.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24 md:pb-8" data-testid="store-transfer-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4">
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
            Transferências de Stock (TAR)
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            {canChooseAnySource
              ? 'Envia stock entre quaisquer armazéns e confirma a chegada de transferências pendentes.'
              : userAllowedWarehouses.length > 1
                ? `Envia stock dos teus armazéns (${userAllowedWarehouses.join(', ')}) para qualquer destino.`
                : `Envia stock do teu armazém${sourceCode ? ` (${sourceCode})` : ''} ou confirma a chegada de transferências.`}
          </p>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 space-y-4 sm:space-y-6">
        <Tabs defaultValue="outbox" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:inline-grid">
          <TabsTrigger value="outbox" data-testid="tab-outbox" className="flex items-center gap-1.5">
            <Send className="h-3.5 w-3.5" />
            Enviar TAR
          </TabsTrigger>
          <TabsTrigger value="inbox" data-testid="tab-inbox" className="flex items-center gap-1.5">
            <Inbox className="h-3.5 w-3.5" />
            Receber TAR
            {inboxPendingCount > 0 && (
              <span
                className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold"
                data-testid="inbox-badge"
              >
                {inboxPendingCount}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── OUTBOX TAB ────────────────────────────────────────────────── */}
        <TabsContent value="outbox" className="mt-4 space-y-4 sm:space-y-6">
          {/* Source → Target */}
          <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Armazéns</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              {canChooseAnySource ? (
                // Admin can choose ANY warehouse as source
                <>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Origem</p>
                  <Select value={sourceCode} onValueChange={changeSource}>
                    <SelectTrigger data-testid="source-warehouse-select">
                      <SelectValue placeholder="Escolher armazém de origem..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {allWarehouses.map((w) => (
                        <SelectItem key={w.code} value={w.code} data-testid={`source-option-${w.code}`}>
                          <div className="flex items-center gap-2">
                            <Building2 className="h-3.5 w-3.5 text-slate-400" />
                            <span className="font-mono text-sm">{w.code}</span>
                            {w.name && w.name !== w.code && (
                              <span className="text-xs text-slate-500 truncate max-w-[180px]">{w.name}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              ) : userAllowedWarehouses.length > 1 ? (
                // User has multiple allowed warehouses - show dropdown but restricted
                <>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Origem (meus armazéns)</p>
                  <Select value={sourceCode} onValueChange={changeSource}>
                    <SelectTrigger data-testid="source-warehouse-select">
                      <SelectValue placeholder="Escolher armazém de origem..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {allWarehouses
                        .filter(w => userAllowedWarehouses.includes(w.code))
                        .map((w) => (
                          <SelectItem key={w.code} value={w.code} data-testid={`source-option-${w.code}`}>
                            <div className="flex items-center gap-2">
                              <Building2 className="h-3.5 w-3.5 text-slate-400" />
                              <span className="font-mono text-sm">{w.code}</span>
                              {w.name && w.name !== w.code && (
                                <span className="text-xs text-slate-500 truncate max-w-[180px]">{w.name}</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </>
              ) : (
                // User has only one warehouse - show fixed (locked)
                <div className="bg-slate-50 rounded-lg p-3 border">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">Origem (o meu armazém)</p>
                  <p className="font-mono text-base font-bold text-slate-900 mt-0.5" data-testid="source-warehouse">
                    {sourceCode}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{user?.default_warehouse_name || ''}</p>
                </div>
              )}
            </div>
            <ArrowRightLeft className="h-5 w-5 text-slate-400 mx-auto sm:mx-0 rotate-90 sm:rotate-0" />
            <div className="flex-1">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Destino</p>
              <Select value={targetCode} onValueChange={setTargetCode}>
                <SelectTrigger data-testid="target-warehouse-select">
                  <SelectValue placeholder="Escolher armazém de destino..." />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {warehouses.map((w) => (
                    <SelectItem key={w.code} value={w.code} data-testid={`target-option-${w.code}`}>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-3.5 w-3.5 text-slate-400" />
                        <span className="font-mono text-sm">{w.code}</span>
                        {w.name && w.name !== w.code && (
                          <span className="text-xs text-slate-500 truncate max-w-[180px]">— {w.name}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search + add items */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Artigos a transferir</CardTitle>
          <CardDescription className="text-xs">Apenas artigos com stock no armazém {sourceCode} aparecem.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Pesquisar SKU, nome ou EAN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-10"
                data-testid="product-search-input"
              />
              {searching && (
                <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-300" />
              )}
            </div>
            <Button
              variant="outline"
              className="h-10 px-3"
              onClick={() => setShowScanner(true)}
              disabled={!sourceCode || scannerLoading}
              title="Ler código de barras"
              data-testid="barcode-scanner-btn"
            >
              <Camera className="h-4 w-4" />
            </Button>
          </div>

          {/* Search dropdown */}
          {searchResults.length > 0 && (
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-80 sm:max-h-96 overflow-y-auto" data-testid="search-results">
              {searchResults.map((p) => {
                const stock = sourceStockFor(p);
                return (
                  <button
                    key={p.id || p.sku}
                    onClick={() => addToCart(p)}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 active:bg-slate-100 flex items-center gap-3"
                    data-testid={`search-result-${p.sku}`}
                  >
                    <Package className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{p.name}</p>
                      <p className="text-[11px] font-mono text-slate-400">{p.sku}</p>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 font-mono">
                      {stock}
                    </Badge>
                  </button>
                );
              })}
            </div>
          )}

          {/* Cart */}
          {cart.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400 border border-dashed rounded-lg">
              Sem artigos. Pesquisa em cima para adicionar.
            </div>
          ) : (
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {/* Mobile cards */}
              <ul className="md:hidden divide-y divide-slate-100" data-testid="transfer-cart-mobile">
                {cart.map((c) => (
                  <li key={c.sku} className="p-3" data-testid={`cart-item-${c.sku}`}>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{c.name}</p>
                        <p className="text-[11px] font-mono text-slate-400">{c.sku} · stock disponível {c.sourceStock}</p>
                      </div>
                      <button onClick={() => removeFromCart(c.sku)} className="text-red-500 active:bg-red-50 p-1 rounded" data-testid={`remove-${c.sku}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <button onClick={() => updateQty(c.sku, -1)} className="h-9 w-9 rounded-lg bg-slate-100 active:bg-slate-200 font-bold text-lg" data-testid={`dec-${c.sku}`}>−</button>
                      <span className="text-lg font-bold tabular-nums w-12 text-center" data-testid={`qty-${c.sku}`}>{c.quantity}</span>
                      <button onClick={() => updateQty(c.sku, +1)} className="h-9 w-9 rounded-lg bg-slate-100 active:bg-slate-200 font-bold text-lg" data-testid={`inc-${c.sku}`}>+</button>
                    </div>
                  </li>
                ))}
              </ul>
              {/* Desktop table */}
              <Table className="hidden md:table">
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead className="text-right">Disp.</TableHead>
                    <TableHead className="text-right">Qtd.</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cart.map((c) => (
                    <TableRow key={c.sku}>
                      <TableCell className="font-mono text-sm">{c.sku}</TableCell>
                      <TableCell className="max-w-md truncate">{c.name}</TableCell>
                      <TableCell className="text-right text-slate-500 font-mono">{c.sourceStock}</TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-2">
                          <button onClick={() => updateQty(c.sku, -1)} className="h-7 w-7 rounded border border-slate-200 hover:bg-slate-50 font-bold">−</button>
                          <span className="font-mono w-8 text-center" data-testid={`qty-desktop-${c.sku}`}>{c.quantity}</span>
                          <button onClick={() => updateQty(c.sku, +1)} className="h-7 w-7 rounded border border-slate-200 hover:bg-slate-50 font-bold">+</button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <button onClick={() => removeFromCart(c.sku)} className="text-red-500 hover:bg-red-50 p-1 rounded">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <div>
            <p className="text-xs text-slate-500 mb-1">Observações (opcional)</p>
            <Textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Notas que aparecerão no documento SAGE..."
              rows={2}
              data-testid="observacoes-input"
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-2">
            <div className="text-xs text-slate-500">
              {cart.length} artigo(s) · <strong className="text-slate-700">{totalItems}</strong> unidades
            </div>
            <Button
              onClick={submitTransfer}
              disabled={submitting || !targetCode || cart.length === 0}
              className="bg-red-600 hover:bg-red-700"
              data-testid="submit-transfer-btn"
            >
              {submitting ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> A enviar para SAGE...</>
              ) : (
                <><Send className="h-4 w-4 mr-2" /> Gerar TAR no SAGE</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card>
        <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>
            <CardTitle className="text-sm sm:text-base">Histórico de TARs enviadas</CardTitle>
            <CardDescription className="text-xs">Últimas 20 transferências geradas</CardDescription>
          </div>
          <Button variant="outline" size="icon" onClick={fetchHistory} disabled={loadingHistory} data-testid="refresh-history-btn" className="h-9 w-9 self-end sm:self-auto">
            <RefreshCw className={`h-4 w-4 ${loadingHistory ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {history.length === 0 ? (
            <p className="px-4 pb-4 sm:px-6 sm:pb-6 text-sm text-slate-400 text-center">Sem transferências ainda.</p>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="transfer-history-list">
              {history.map((t) => {
                const totalQty = (t.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
                const status = t.status || 'pending';
                const isExpanded = expandedTransferId === t.id;
                return (
                  <li key={t.id} className="px-3 py-3 sm:px-4" data-testid={`history-${t.id}`}>
                    {/* Mobile-first card layout */}
                    <div className="flex flex-col gap-2">
                      {/* Top row: warehouses + expand button */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                          <span className="font-mono text-sm font-medium text-slate-900">{t.source_warehouse}</span>
                          <ArrowRightLeft className="h-3 w-3 text-slate-400 flex-shrink-0" />
                          <span className="font-mono text-sm font-medium text-slate-900">{t.target_warehouse}</span>
                        </div>
                        <button
                          onClick={() => setExpandedTransferId(isExpanded ? null : t.id)}
                          className="p-1.5 rounded hover:bg-slate-100 active:bg-slate-200 text-slate-500 flex-shrink-0"
                          title={isExpanded ? 'Ocultar artigos' : 'Ver artigos'}
                          data-testid={`toggle-items-${t.id}`}
                        >
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>
                      
                      {/* Target name if different */}
                      {t.target_warehouse_name && t.target_warehouse_name !== t.target_warehouse && (
                        <p className="text-xs text-slate-500 truncate -mt-1 pl-5">→ {t.target_warehouse_name}</p>
                      )}
                      
                      {/* Badge + meta info row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 font-mono text-[10px] sm:text-[11px]">
                          🔗 {t.sage_document_reference}
                        </Badge>
                        <span className="text-[11px] text-slate-500">
                          {(t.items || []).length} art. · <strong>{totalQty}</strong> un
                        </span>
                      </div>
                      
                      {/* Date */}
                      <p className="text-[11px] text-slate-400">
                        {new Date(t.ts).toLocaleString('pt-PT')}
                      </p>
                      
                      {/* Status messages */}
                      {status === 'received' && (
                        <p className="text-[11px] text-emerald-600 flex items-center gap-1">
                          ✓ Recebido {new Date(t.received_at).toLocaleDateString('pt-PT')} por {t.received_by_email?.split('@')[0]}
                        </p>
                      )}
                      {status === 'with_discrepancy' && (
                        <p className="text-[11px] text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                          Discrepância — {new Date(t.received_at).toLocaleDateString('pt-PT')}
                        </p>
                      )}
                      {status === 'pending' && (
                        <p className="text-[11px] text-slate-400">⏳ A aguardar receção</p>
                      )}
                      {t.observacoes && (
                        <p className="text-[11px] text-slate-400 italic truncate">"{t.observacoes}"</p>
                      )}
                    </div>
                    
                    {/* Expanded items list */}
                    {isExpanded && (t.items || []).length > 0 && (
                      <div className="mt-3 bg-slate-50 rounded-lg p-2.5 sm:p-3 border border-slate-200">
                        <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                          <Package className="h-3.5 w-3.5" />
                          Artigos ({(t.items || []).length})
                        </p>
                        <ul className="space-y-1.5">
                          {(t.items || []).map((item, idx) => (
                            <li key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs bg-white rounded px-2 py-1.5 border border-slate-100 gap-0.5 sm:gap-2">
                              <div className="min-w-0">
                                <span className="font-mono text-slate-700 text-[11px]">{item.sku}</span>
                                {item.name && (
                                  <span className="text-slate-500 block sm:inline sm:ml-1 truncate text-[11px]">{item.name}</span>
                                )}
                              </div>
                              <span className="font-mono font-medium text-slate-800 text-right">{item.quantity} un</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* ── INBOX TAB ─────────────────────────────────────────────────── */}
        <TabsContent value="inbox" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
                <div>
                  <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                    <Inbox className="h-4 w-4 text-red-600" />
                    {canChooseAnySource ? 'Pendentes (todos)' : `Para ${sourceCode}`}
                  </CardTitle>
                  <CardDescription className="text-xs mt-1">
                    Confirma a receção física dos artigos.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <label className="flex items-center gap-1.5 text-[11px] sm:text-xs text-slate-600 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={includeReceived}
                      onChange={(e) => { setIncludeReceived(e.target.checked); fetchInbox(e.target.checked); }}
                      className="h-4 w-4 rounded border-slate-300"
                      data-testid="inbox-show-received-toggle"
                    />
                    Recebidas
                  </label>
                  <Button variant="outline" size="icon" onClick={() => fetchInbox()} disabled={loadingInbox} data-testid="refresh-inbox-btn" className="h-9 w-9">
                    <RefreshCw className={`h-4 w-4 ${loadingInbox ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {inbox.length === 0 ? (
                <p className="px-4 pb-4 sm:px-6 sm:pb-6 text-sm text-slate-400 text-center" data-testid="inbox-empty">
                  {includeReceived ? 'Sem transferências para este armazém.' : '🎉 Sem transferências pendentes.'}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="inbox-list">
                  {inbox.map((t) => {
                    const totalQty = (t.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
                    const status = t.status || 'pending';
                    const isPending = status === 'pending' || !t.status;
                    const isExpanded = expandedTransferId === `inbox-${t.id}`;
                    return (
                      <li key={t.id} className="px-3 py-3 sm:px-4" data-testid={`inbox-${t.id}`}>
                        {/* Mobile-first card layout */}
                        <div className="flex flex-col gap-2">
                          {/* Top row: warehouses + expand */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                              <span className="font-mono text-sm font-medium text-slate-900">{t.source_warehouse}</span>
                              <ArrowRightLeft className="h-3 w-3 text-slate-400 flex-shrink-0" />
                              <span className="font-mono text-sm font-medium text-slate-900">{t.target_warehouse}</span>
                            </div>
                            <button
                              onClick={() => setExpandedTransferId(isExpanded ? null : `inbox-${t.id}`)}
                              className="p-1.5 rounded hover:bg-slate-100 active:bg-slate-200 text-slate-500 flex-shrink-0"
                              title={isExpanded ? 'Ocultar artigos' : 'Ver artigos'}
                              data-testid={`toggle-inbox-items-${t.id}`}
                            >
                              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </button>
                          </div>
                          
                          {/* Badge + meta info */}
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 font-mono text-[10px] sm:text-[11px]">
                              🔗 {t.sage_document_reference}
                            </Badge>
                            <span className="text-[11px] text-slate-500">
                              {(t.items || []).length} art. · <strong>{totalQty}</strong> un
                            </span>
                          </div>
                          
                          {/* Sender and date - compact for mobile */}
                          <p className="text-[11px] text-slate-400">
                            de {t.actor_email?.split('@')[0]} · {new Date(t.ts).toLocaleDateString('pt-PT')}
                          </p>
                          
                          {t.observacoes && (
                            <p className="text-[11px] text-slate-400 italic truncate">"{t.observacoes}"</p>
                          )}
                          
                          {/* Status messages */}
                          {status === 'received' && (
                            <p className="text-[11px] text-emerald-600 flex items-center gap-1">
                              <CheckCircle2 className="h-3 w-3 flex-shrink-0" /> 
                              Recebido {new Date(t.received_at).toLocaleDateString('pt-PT')} por {t.received_by_email?.split('@')[0]}
                            </p>
                          )}
                          {status === 'with_discrepancy' && (
                            <p className="text-[11px] text-amber-600 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                              Discrepância — {t.received_by_email?.split('@')[0]}
                            </p>
                          )}
                          
                          {/* Expanded items list */}
                          {isExpanded && (t.items || []).length > 0 && (
                            <div className="mt-1 bg-slate-50 rounded-lg p-2.5 sm:p-3 border border-slate-200">
                              <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5">
                                <Package className="h-3.5 w-3.5" />
                                Artigos ({(t.items || []).length})
                              </p>
                              <ul className="space-y-1.5">
                                {(t.items || []).map((item, idx) => (
                                  <li key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs bg-white rounded px-2 py-1.5 border border-slate-100 gap-0.5 sm:gap-2">
                                    <div className="min-w-0">
                                      <span className="font-mono text-slate-700 text-[11px]">{item.sku}</span>
                                      {item.name && (
                                        <span className="text-slate-500 block sm:inline sm:ml-1 truncate text-[11px]">{item.name}</span>
                                      )}
                                    </div>
                                    <span className="font-mono font-medium text-slate-800 text-right">{item.quantity} un</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Action button - full width on mobile */}
                          {isPending ? (
                            <Button
                              onClick={() => openReceiptDialog(t)}
                              className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 w-full sm:w-auto h-10 mt-1"
                              data-testid={`confirm-receipt-${t.id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-2" />
                              Confirmar receção
                            </Button>
                          ) : (
                            <Badge
                              className={`self-start mt-1 ${
                                status === 'with_discrepancy'
                                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                                  : 'bg-emerald-100 text-emerald-700 border-emerald-200'
                              }`}
                            >
                              {status === 'with_discrepancy' ? 'Discrepância' : 'Recebido'}
                            </Badge>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Receipt confirmation dialog ─────────────────────────────────── */}
      <Dialog open={!!receiptTransfer} onOpenChange={(o) => !o && setReceiptTransfer(null)}>
        <DialogContent
          className="w-[calc(100vw-1.5rem)] max-w-md max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden rounded-xl"
          data-testid="receipt-dialog"
        >
          <DialogHeader className="px-4 pt-4 pb-3 sm:px-5 sm:pt-5 border-b border-slate-100 text-left space-y-1">
            <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              Confirmar receção
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Confere as quantidades fisicamente recebidas. Discrepâncias ficam registadas em auditoria.
            </DialogDescription>
          </DialogHeader>

          {receiptTransfer && (
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 sm:px-5 space-y-3">
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="font-mono text-sm font-semibold text-slate-800 flex items-center gap-1.5 flex-wrap">
                  <span>{receiptTransfer.source_warehouse}</span>
                  <ArrowRightLeft className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                  <span>{receiptTransfer.target_warehouse}</span>
                </p>
                <p className="text-[11px] text-slate-500 mt-1 break-all">🔗 {receiptTransfer.sage_document_reference}</p>
              </div>

              <div className="space-y-2" data-testid="receipt-items">
                {receiptItems.map((i) => (
                  <div
                    key={i.sku}
                    className={`p-3 border rounded-lg ${
                      i.received === i.expected ? 'bg-white border-slate-200' : 'bg-amber-50/50 border-amber-300'
                    }`}
                  >
                    <p className="text-sm font-medium text-slate-900 leading-snug">{i.name}</p>
                    <div className="flex items-center justify-between gap-2 mt-2">
                      <p className="text-[11px] font-mono text-slate-400 min-w-0 truncate">
                        {i.sku} · esperado: <span className="font-semibold text-slate-600">{i.expected}</span>
                      </p>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => updateReceiptQty(i.sku, i.received - 1)}
                          disabled={i.received <= 0}
                          className="h-10 w-10 rounded-lg border border-slate-200 bg-white text-slate-600 text-lg font-semibold active:bg-slate-100 disabled:opacity-40 flex items-center justify-center"
                          data-testid={`receipt-qty-minus-${i.sku}`}
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="0"
                          inputMode="numeric"
                          value={i.received}
                          onChange={(e) => updateReceiptQty(i.sku, e.target.value)}
                          className={`w-14 h-10 px-1 rounded-lg border text-center text-base font-mono font-semibold ${
                            i.received === i.expected
                              ? 'border-slate-200 bg-white text-slate-800'
                              : 'border-amber-400 bg-amber-50 text-amber-700'
                          }`}
                          data-testid={`receipt-qty-${i.sku}`}
                        />
                        <button
                          type="button"
                          onClick={() => updateReceiptQty(i.sku, i.received + 1)}
                          className="h-10 w-10 rounded-lg border border-slate-200 bg-white text-slate-600 text-lg font-semibold active:bg-slate-100 flex items-center justify-center"
                          data-testid={`receipt-qty-plus-${i.sku}`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Textarea
                value={receiptNotes}
                onChange={(e) => setReceiptNotes(e.target.value)}
                placeholder={hasDiscrepancy ? "Descreve a discrepância (recomendado)..." : "Notas (opcional)..."}
                rows={2}
                className="text-sm"
                data-testid="receipt-notes-input"
              />

              {hasDiscrepancy && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    Pelo menos um artigo difere do esperado. A transferência será marcada com <strong>discrepância</strong>.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="px-4 py-3 sm:px-5 border-t border-slate-100 bg-white flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setReceiptTransfer(null)}
              disabled={confirmingReceipt}
              className="w-full sm:w-auto h-11"
            >
              Cancelar
            </Button>
            <Button
              onClick={submitReceipt}
              disabled={confirmingReceipt}
              className={`w-full sm:w-auto h-11 ${hasDiscrepancy ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              data-testid="submit-receipt-btn"
            >
              {confirmingReceipt ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> A confirmar...</>
              ) : (
                <><CheckCircle2 className="h-4 w-4 mr-2" /> {hasDiscrepancy ? 'Confirmar com discrepância' : 'Confirmar receção'}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Barcode Scanner */}
      {showScanner && (
        <FullscreenScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}
      </div>
    </div>
  );
}
